import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import { useEffect } from 'react';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
// Sign-up flow v2 supersedes RegisterPage + OnboardingPage at /register and
// /onboarding (the old files stay in the tree untouched for reference).
import SignupV2Page, { SignupResumeCatcher } from './pages/signup-v2/SignupV2Page';
import SubscribePage from './pages/SubscribePage';
import DashboardPage from './pages/DashboardPage';
import SalesPage from './pages/SalesPage';
import LocationsPage from './pages/LocationsPage';
import MarginsPage from './pages/MarginsPage';
import LiveSalesPage from './pages/LiveSalesPage';
import EndOfDayPage from './pages/EndOfDayPage';
import StaffPage from './pages/StaffPage';
import MessagesPage from './pages/MessagesPage';
import LogbookPage from './pages/LogbookPage';
import OnboardingSettingsPage from './pages/OnboardingSettingsPage';
import MenuPage from './pages/MenuPage';
import ReservationsPage from './pages/ReservationsPage';
import GuestsPage from './pages/GuestsPage';
import InventoryPage from './pages/InventoryPage';
import CountsPage from './pages/CountsPage';
import OrderingPage from './pages/OrderingPage';
import ReceivingPage from './pages/ReceivingPage';
import InvoicesPage from './pages/InvoicesPage';
import FoodCostPage from './pages/FoodCostPage';
import RecipesPage from './pages/RecipesPage';
import WholesalePage from './pages/WholesalePage';
import ProductionPage from './pages/ProductionPage';
import ImportsPage from './pages/ImportsPage';
import AccountsPage from './pages/AccountsPage';
import ProductsPage from './pages/ProductsPage';
import PayrollPage from './pages/PayrollPage';
import ReportsPage from './pages/ReportsPage';
import PnlPage from './pages/PnlPage';
import ShiftsPage from './pages/ShiftsPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import EventBeoPage from './pages/EventBeoPage';
import ShiftSetupPage from './pages/ShiftSetupPage';
import ScheduleBuilderPage from './pages/ScheduleBuilderPage';
import LaborPage from './pages/LaborPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AdminPage from './pages/AdminPage';
import ConnectBankPage from './pages/ConnectBankPage';
import HelpPage from './pages/HelpPage';
import HostPage from './pages/HostPage';
import TrainingsPage from './pages/TrainingsPage';
import FloorPlanPage from './pages/FloorPlanPage';
import NativeSubscriptionNotice from './components/NativeSubscriptionNotice';
import { isNativeApp } from './lib/platform';

// Shopify sends merchants here after "Install" with ?shop=<store>.myshopify.com.
// Logged-in users go straight into OAuth for that store; everyone else logs in
// first and the pending shop is picked up right after.
function ShopifyInstallCatcher() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const claim = params.get('shopifyShop');
    if (claim && /\.myshopify\.com$/.test(claim)) sessionStorage.setItem('pending_shopify_claim', claim);
    const shop = params.get('shop');
    if (!shop || !/\.myshopify\.com$/.test(shop)) return;
    const token = localStorage.getItem('manager_token');
    if (!token) {
      sessionStorage.setItem('pending_shopify_shop', shop);
      return;
    }
    import('./services/api').then(({ default: api }) =>
      api.get(`/v1/production/shopify/oauth/start?shop=${encodeURIComponent(shop)}`)
        .then((r: any) => { const url = r.data?.data?.url; if (url) window.location.href = url; })
        .catch(() => undefined));
  }, []);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ShopifyInstallCatcher />
      <SignupResumeCatcher />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Account registration is web-only (App Store Guideline 3.1.1). */}
        <Route path="/register" element={isNativeApp ? <Navigate to="/login" replace /> : <SignupV2Page />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/connect-bank" element={<ConnectBankPage />} />

        {/* Onboarding (requires auth but not onboarded/subscribed) — the same
            sign-up v2 flow, resuming from its saved step (OAuth round-trips
            and the old register path land here). */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute requireOnboarded={false} requireSubscription={false}>
              <SignupV2Page />
            </ProtectedRoute>
          }
        />

        {/* Subscribe (requires auth and onboarded, but not subscription) */}
        <Route
          path="/subscribe"
          element={
            <ProtectedRoute requireOnboarded={true} requireSubscription={false}>
              {/* Native shows no prices/checkout (Guideline 3.1.1); web keeps Stripe flow. */}
              {isNativeApp ? <NativeSubscriptionNotice /> : <SubscribePage />}
            </ProtectedRoute>
          }
        />

        {/* BEO / proposal print view — protected but chrome-free so the
            printed page is just the document (print-to-PDF pattern) */}
        <Route
          path="/events/:id/beo"
          element={
            <ProtectedRoute>
              <EventBeoPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes (require auth, onboarded, AND subscription) */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="margins" element={<MarginsPage />} />
          <Route path="sales-live" element={<LiveSalesPage />} />
          <Route path="sales" element={<SalesPage />} />
          <Route path="end-of-day" element={<EndOfDayPage />} />
          <Route path="staff" element={<StaffPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="logbook" element={<LogbookPage />} />
          <Route path="staff/onboarding-settings" element={<OnboardingSettingsPage />} />
          <Route path="menu" element={<MenuPage />} />
          <Route path="reservations" element={<ReservationsPage />} />
          <Route path="guests" element={<GuestsPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="counts" element={<CountsPage />} />
          <Route path="ordering" element={<OrderingPage />} />
          <Route path="receiving" element={<ReceivingPage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="food-cost" element={<FoodCostPage />} />
          <Route path="recipes" element={<RecipesPage />} />
          <Route path="wholesale" element={<WholesalePage />} />
          <Route path="production" element={<ProductionPage />} />
          <Route path="imports" element={<ImportsPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="payroll" element={<PayrollPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="pnl" element={<PnlPage />} />
          <Route path="shifts" element={<ShiftsPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="events/:id" element={<EventDetailPage />} />
          <Route path="shifts/:instanceId/setup" element={<ShiftSetupPage />} />
          <Route path="schedule-builder" element={<ScheduleBuilderPage />} />
          <Route path="labor" element={<LaborPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="billing" element={isNativeApp ? <NativeSubscriptionNotice /> : <BillingPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="host" element={<HostPage />} />
          <Route path="floor-plan" element={<FloorPlanPage />} />
          <Route path="trainings" element={<TrainingsPage />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
